import { useToast } from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ProductSearchModalUpdated } from '@jane/business-admin/components/ProductSearchModalUpdated';
import type { ProductSearchSelection } from '@jane/business-admin/components/ProductSearchModalUpdated';
import { ConditionType } from '@jane/business-admin/components/shared/specials/modal/form';
import {
  useGetGlobalProductDetails,
  useGetProductDetails,
} from '@jane/business-admin/hooks';
import type {
  GlobalSpecialProduct,
  MenuProductForProductsTable,
} from '@jane/business-admin/types';

import { ConditionByModal } from './generic_components/ConditionByModal';
import type { Condition } from './generic_components/ConditionByModal';
import { LoadingCondition } from './generic_components/LoadingCondition';

interface ProductsConditionsProps {
  disableAddButton?: boolean;
  fieldPrefix: string;
  isGlobalSpecial?: boolean;
  type: ConditionType.Products | ConditionType.ExcludeProducts;
}

export const ProductsConditions = ({
  disableAddButton,
  fieldPrefix,
  isGlobalSpecial,
  type,
}: ProductsConditionsProps) => {
  const { setValue, watch } = useFormContext();
  const rulesWatch = watch(`${fieldPrefix}.rules`);
  const toast = useToast();
  const [productSearchModalOpen, setProductSearchModalOpen] = useState(false);

  const includesOrExcludes =
    type === ConditionType.Products ? 'includes' : 'excludes';
  const productsCreatedWithinDaysWatch =
    rulesWatch?.includes?.[0]?.products_created_within_days || null; // flatten a potential `''`

  const productIds: number[] = useMemo(() => {
    if (rulesWatch && rulesWatch[includesOrExcludes]) {
      // rules.includes and rules.excludes are arrays, so we pull all product_ids out of all the nested arrays
      const productIds = rulesWatch[includesOrExcludes].reduce(
        (accumulator: number[], currentValue: { product_ids: number[] }) => {
          return currentValue.product_ids
            ? accumulator.concat(currentValue.product_ids)
            : accumulator;
        },
        []
      );
      return productIds;
    } else {
      return [];
    }
  }, [rulesWatch, includesOrExcludes]);

  const {
    data: productData,
    hasError: getProductsError,
    isLoading,
  } = useGetProducts({ query: { productIds }, isGlobalSpecial });

  const formatLabel = useCallback(
    (product: GlobalSpecialProduct | MenuProductForProductsTable) => {
      const productLabels = [
        isGlobalSpecial
          ? get(product, 'kind', '')
          : get(product, 'category', ''),
        get(product, 'brand', ''),
        get(product, 'name', ''),
      ];

      return productLabels.filter(Boolean).join('/');
    },
    [isGlobalSpecial]
  );

  const productDetails: Condition[] = useMemo(() => {
    if (!productData) return [];
    return productData.map((product) => {
      const formattedLabel = formatLabel(product);
      return {
        menu_product_id: product['id'],
        value: product['product_id']?.toString() || '',
        label: formattedLabel,
      };
    });
  }, [productData, formatLabel]);

  const selectedProducts: ProductSearchSelection[] = useMemo(() => {
    if (!productData) return [];
    return productData.map((product) => ({
      id: product['id'],
      menu_product_id: product['id'],
      product_id: product.product_id?.toString() || '',
    }));
  }, [productData]);

  useEffect(() => {
    if (getProductsError) {
      toast.add({
        label: 'Error getting product details',
        variant: 'error',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProductsError]);

  const updateConditions = ({
    data,
    fromModal,
  }:
    | {
        data: ProductSearchSelection[];
        fromModal: true;
      }
    | {
        data: Condition[];
        fromModal: false;
      }) => {
    let values;
    if (fromModal) {
      values = data.map(({ product_id, id }) => {
        if (isGlobalSpecial) {
          return Number(id);
        }
        return Number(product_id);
      });
    } else {
      values = data.map(({ value }) => Number(value));
    }

    const firstCondition =
      (rulesWatch[includesOrExcludes] && rulesWatch[includesOrExcludes][0]) ||
      {};
    setValue(
      `${fieldPrefix}.rules`,
      {
        ...rulesWatch,
        [includesOrExcludes]: [{ ...firstCondition, product_ids: values }],
      },
      { shouldDirty: true }
    );
  };

  return (
    <>
      {!productDetails ? (
        <LoadingCondition />
      ) : (
        <ConditionByModal
          disableAddButton={disableAddButton}
          onOpenModal={() => setProductSearchModalOpen(true)}
          type={type}
          conditions={productDetails}
          conditionsLoading={isLoading}
          onConditionsUpdate={(data) =>
            updateConditions({ data, fromModal: false })
          }
          lastCondition={type === ConditionType.ExcludeProducts}
        />
      )}
      {productSearchModalOpen && (
        <ProductSearchModalUpdated
          isGlobalSpecial={isGlobalSpecial}
          closeModal={() => setProductSearchModalOpen(false)}
          onlyProductsCreatedWithinDays={productsCreatedWithinDaysWatch}
          onlyShowInStockProducts={false}
          onSubmit={(data) => updateConditions({ data, fromModal: true })}
          selectedProducts={selectedProducts}
        />
      )}
    </>
  );
};

const useGetProducts = ({
  isGlobalSpecial = false,
  query,
}: {
  isGlobalSpecial?: boolean;
  query: {
    productIds?: number[];
  };
}) => {
  const {
    data: globalProductData,
    hasError: hasGlobalError,
    isLoading: isGlobalLoading,
  } = useGetGlobalProductDetails(query.productIds ?? [], isGlobalSpecial);

  const {
    data: productData,
    hasError: hasProductError,
    isLoading: isProductLoading,
  } = useGetProductDetails(query.productIds ?? [], false, !isGlobalSpecial);

  return {
    data: isGlobalSpecial ? globalProductData : productData,
    hasError: isGlobalSpecial ? hasGlobalError : hasProductError,
    isLoading: isGlobalSpecial ? isGlobalLoading : isProductLoading,
  };
};
